import { useState } from 'react'
import { useTranslations } from 'use-intl'
import { Link, useParams } from '@tanstack/react-router'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'
import { AppDetailCard } from '@/components/app-detail-card'
import { CATEGORY_ICONS } from '@/components/product-category'
import { HighlightText } from '@/components/ui/highlight-text'
import { Reveal } from '@/components/ui/reveal'
import {
  APP_ASSETS,
  PEOPLE_APPS,
  PROPERTY_APPS,
  type AppKey,
} from '../data/app-detail'

type Category = 'people' | 'property'

// Feature-page route per product category. The card's `feature` param is the app key.
const FEATURE_ROUTE = {
  people: '/$locale/solutions/people-security/$feature',
  property: '/$locale/solutions/property-security/$feature',
} as const

// All apps in a single row, tagged with their category. Property (compliassist)
// leads the row.
const ALL_APPS: readonly { key: AppKey; category: Category }[] = [
  ...PROPERTY_APPS.map((key) => ({ key, category: 'property' as const })),
  ...PEOPLE_APPS.map((key) => ({ key, category: 'people' as const })),
]

/** Outline category toggle. Active → filled primary to mark the selection. */
function FilterButton({
  category,
  active,
  onClick,
}: {
  category: Category
  active: boolean
  onClick: () => void
}) {
  const t = useTranslations('nav.solutionsItems')
  return (
    <Button
      type="button"
      variant={active ? 'default' : 'outline'}
      onClick={onClick}
      aria-pressed={active}
    >
      <img
        src={CATEGORY_ICONS[category]}
        alt=""
        className={cn('size-5', active && 'brightness-0 invert')}
        aria-hidden
      />
      {t(category)}
    </Button>
  )
}

export function AppDetailSections() {
  const t = useTranslations('home.appDetail')
  const tc = useTranslations('home.appDetail.cards')
  const { locale } = useParams({ from: '/$locale' })
  // Empty = no filter (everything at full opacity). Both can be active at once;
  // clicking an active one clears it.
  const [filters, setFilters] = useState<ReadonlySet<Category>>(new Set())

  return (
    <section className="pb-16 lg:pb-24">
      <Reveal className="mx-auto max-w-7xl px-6 lg:px-8">
        <Carousel opts={{ align: 'start' }}>
          <div className="mx-auto max-w-5xl text-center">
            <h2 className="font-serif text-4xl font-semibold tracking-tight sm:text-5xl lg:whitespace-nowrap">
              <HighlightText>{t('titleHighlight')}.</HighlightText>{' '}
              {t('titleRest')}
            </h2>
            <p className="mx-auto mt-3 max-w-2xl font-mono text-sm text-secondary-foreground">
              {t('subtitle')}
            </p>
          </div>

          <div className="mt-8 flex flex-wrap justify-center gap-3">
            {(['people', 'property'] as const).map((category) => (
              <FilterButton
                key={category}
                category={category}
                active={filters.has(category)}
                onClick={() =>
                  setFilters((f) => {
                    const next = new Set(f)
                    next.has(category)
                      ? next.delete(category)
                      : next.add(category)
                    return next
                  })
                }
              />
            ))}
          </div>

          {/* Chevrons overlay the card row: absolute, vertically centered on the
              cards, flush to their left/right inner edges. */}
          <div className="relative mt-10">
            <CarouselPrevious className="absolute top-1/2 -left-5 z-10 size-10 -translate-y-1/2 rounded-lg border-muted bg-background" />
            <CarouselNext className="absolute top-1/2 -right-5 z-10 size-10 -translate-y-1/2 rounded-lg border-muted bg-background" />
            <CarouselContent>
              {ALL_APPS.map(({ key, category }) => (
                <CarouselItem
                  key={key}
                  className="basis-[85%] sm:basis-1/2 lg:basis-1/3 xl:basis-1/4"
                >
                  <Link
                    to={FEATURE_ROUTE[category] as never}
                    params={{ locale, feature: key } as never}
                    className="block h-full no-underline"
                  >
                    <AppDetailCard
                      logo={APP_ASSETS[key].logo}
                      mock={APP_ASSETS[key].mock}
                      icon={APP_ASSETS[key].icon}
                      desktop={APP_ASSETS[key].desktop}
                      category={category}
                      dimmed={filters.size > 0 && !filters.has(category)}
                      titleLead={tc(`${key}.titleLead`)}
                      titlePrimary={tc(`${key}.titlePrimary`)}
                      description={tc(`${key}.description`)}
                    />
                  </Link>
                </CarouselItem>
              ))}
            </CarouselContent>
          </div>
        </Carousel>
      </Reveal>
    </section>
  )
}
